import { useTranslation } from "next-i18next";
import { useQueryClient } from "react-query";
import { useAddCommentMutation } from "src/api/client/comment-v2.api";
import { TComment } from "src/components/common/comments-area/comment/comment.types";
import { EnumToastStatus, useWfToast } from "src/hooks/use-wf-toast";
import { TCommentsPaginatedDataType } from "../utils/create-initial-replies-page-data";
import { createNewPageBasedOnRootComment } from "../utils/create-new-page-based-on-root-comment";
import { createNewPagesBasedOnReply } from "../utils/create-new-pages-based-on-reply";
import { updateCommentInfiniteQueryCache } from "../utils/update-comment-infinite-query-cache";

type TAddCommentParams = {
    onSuccess: () => void;
    entityId: string;
    comments: TComment[] | undefined;
};
type TUpdateInfiniteQueryCacheAddComment = {
    comments: TComment[] | undefined;
    entityId: string;
};

const useUpdateInfiniteQueryCacheAddComment = ({ comments, entityId }: TUpdateInfiniteQueryCacheAddComment) => {
    const queryClient = useQueryClient();

    const addComment = (comment: TComment) => {
        const newComment = { ...comment, ignoreCounting: true };

        const handleAddReplyComment = (data: TCommentsPaginatedDataType | undefined) => {
            return data?.pages
                ? createNewPagesBasedOnReply({ data, newComment })
                : createNewPageBasedOnRootComment({ comments, newComment }) || undefined;
        };
        const handleAddRootComment = (data: TCommentsPaginatedDataType | undefined) => {
            if (data?.pages) {
                const [firstPage, ...restPages] = data.pages;
                const updatedFirstPage = { comments: [newComment, ...firstPage.comments], commentsCount: data.pages[0].commentsCount };

                return {
                    pages: [updatedFirstPage, ...restPages],
                    pageParams: data.pageParams,
                };
            }
        };

        updateCommentInfiniteQueryCache({
            queryClient,
            comment: newComment,
            entityId,
            handleRootComment: handleAddRootComment,
            handleReplyComment: handleAddReplyComment,
        });
    };

    return { addComment };
};

export const useAddComment = ({ onSuccess, entityId, comments }: TAddCommentParams) => {
    const toast = useWfToast();
    const { t } = useTranslation(["common", "comment"]);

    const { addComment: addCommentToInfiniteQueryCache } = useUpdateInfiniteQueryCacheAddComment({ comments, entityId });

    const { mutatePromise: addComment, isLoading } = useAddCommentMutation({
        onSuccess: newComment => {
            addCommentToInfiniteQueryCache(newComment);
            onSuccess();

            toast({ status: EnumToastStatus.Success, title: t("toast.comment-saved-successfully", { ns: "comment" }) });
        },
        onError: () => {
            toast({ status: EnumToastStatus.Error, title: t("general-error") });
        },
    });

    return { addComment, isLoading };
};
